import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    BadgeCheck,
    Clock3,
    MapPin,
    Phone,
    Siren
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { ImageReveal } from '@/components/motion/image-reveal'
import { Reveal } from '@/components/motion/reveal'
import { CountUp } from '@/components/motion/count-up'
import { ScrubText } from '@/components/motion/scrub-text'
import { ReviewCard, Stars } from '@/components/widgets/review-card'
import { aboutReviews, companyStats, reviewStats, values } from '@/constants/trust'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

const topMention = Math.max(...reviewStats.mentions.map(mention => mention.count))

const visitDetails: { icon: LucideIcon, label: string, value: string, href?: string }[] = [
    { icon: MapPin, label: "Where we're based", value: siteConfig.address },
    { icon: Clock3, label: "Office open", value: siteConfig.hours },
    { icon: Siren, label: "After hours", value: siteConfig.emergencyHours },
    { icon: Phone, label: "Call us", value: siteConfig.phone, href: siteConfig.phoneHref },
    { icon: BadgeCheck, label: "License and insurance", value: `${siteConfig.license} · ${siteConfig.insured}` }
]

const AboutPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description="Repairing and installing garage doors in the Riverton metro since 2011. Six trucks, nine technicians and one rule: price first, replace only what's broken."
            />

            <PageHero
                eyebrow="Our story"
                title="One truck, one winding bar and a simple rule."
                accent={['simple', 'rule.']}
                description="Sam Okafor spent nine years as a commercial door technician and watched too many homeowners pay for parts they didn't need. He started Liftline in 2011 to do it differently. We now run six trucks and nine technicians, and the rule he began with still holds: the price comes first, and we replace only what's broken."
                actions={(
                    <>
                        <ButtonLink href={QUOTE_PATH} size='lg'>Request a price</ButtonLink>
                        <ButtonLink href='#reviews' variant='outline' size='lg' icon='none'>See what customers say</ButtonLink>
                    </>
                )}
                aside={(
                    <div className='relative pb-8 sm:pb-0'>
                        <ImageReveal
                            src='/images/services/emergency-repair.jpg'
                            alt="Liftline technician adjusting a torsion spring above a garage door"
                            className='aspect-[4/5] rounded-[32px]'
                            sizes='(min-width: 1024px) 38vw, 100vw'
                            reveal={false}
                            priority
                        />

                        <div className='absolute -bottom-2 left-5 flex items-center gap-4 rounded-[22px] bg-ink p-5 pr-7 text-cream shadow-[0_30px_60px_-30px_rgba(21,23,26,0.7)] sm:-bottom-6 sm:-left-6'>
                            <span className='font-heading text-[2.4rem] font-semibold leading-none tracking-[-0.05em]'>{siteConfig.founded}</span>

                            <span className='text-[13.5px] leading-snug text-cream/65'>
                                Repairing doors across<br />the {siteConfig.region}
                            </span>
                        </div>
                    </div>
                )}
            />

            <section id='numbers' className='relative overflow-hidden bg-ink py-20 text-cream md:py-24'>
                <div aria-hidden='true' className='door-grooves pointer-events-none absolute inset-0 opacity-30' />

                <Container className='relative'>
                    <Reveal selector=':scope > div' as='dl' className='grid grid-cols-2 gap-y-12 lg:grid-cols-4'>
                        {companyStats.map((stat, index) => (
                            <div
                                key={stat.label}
                                className={cn(
                                    'flex flex-col-reverse gap-3 px-1 sm:px-6',
                                    index % 2 === 1 && 'border-l border-cream/10',
                                    index > 0 && 'lg:border-l lg:border-cream/10'
                                )}
                            >
                                <dt className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50'>{stat.label}</dt>

                                <dd className='font-heading text-[3.2rem] font-semibold leading-none tracking-[-0.05em] sm:text-[4.5rem] lg:text-[5.25rem]'>
                                    {stat.label === "Founded"
                                        ? stat.display
                                        : <CountUp value={stat.value} suffix={stat.suffix} />}
                                </dd>
                            </div>
                        ))}
                    </Reveal>
                </Container>
            </section>

            <section id='our-rule' className='py-24 md:py-36'>
                <Container>
                    <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-slate'>Unchanged since {siteConfig.founded}</p>

                    <ScrubText
                        as='h2'
                        text="The price comes first. Only broken parts get replaced."
                        accent={['first.', 'broken']}
                        className='mt-8 max-w-[18ch] font-heading text-display font-semibold text-ink'
                    />
                </Container>
            </section>

            <section id='values' className='pb-24 md:pb-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-4'>
                        <div className='lg:sticky lg:top-32'>
                            <SectionHeading
                                layout='stack'
                                eyebrow="On every job"
                                title="Three things we never skip."
                                titleClassName='max-w-[12ch]'
                            />
                        </div>
                    </div>

                    <Reveal selector=':scope > li' as='ol' className='border-t border-ink/10 lg:col-span-8'>
                        {values.map((value, index) => (
                            <li key={value.title} className='group grid gap-4 border-b border-ink/10 py-10 sm:grid-cols-12 sm:gap-6 md:py-12'>
                                <span className='font-mono text-[12px] text-slate sm:col-span-2 sm:pt-3'>{pad(index + 1)}</span>

                                <h3 className='font-heading text-[2.2rem] font-semibold leading-none tracking-[-0.04em] text-ink transition-colors duration-500 group-hover:text-ember sm:col-span-5 md:text-[2.8rem]'>
                                    {value.title}
                                </h3>

                                <p className='text-[16px] leading-relaxed text-slate sm:col-span-5 sm:pt-2'>
                                    {value.body}
                                </p>
                            </li>
                        ))}
                    </Reveal>
                </Container>
            </section>

            <section id='reviews' className='scroll-mt-24 bg-sand/60 py-24 md:py-32'>
                <Container>
                    <SectionHeading
                        eyebrow="What customers say"
                        title={`What ${reviewStats.count} reviews keep coming back to.`}
                        titleClassName='max-w-[16ch]'
                    />

                    <div className='mt-14 grid gap-4 lg:grid-cols-12'>
                        <Reveal className='rounded-[26px] bg-ink p-7 text-cream sm:p-8 lg:col-span-4'>
                            <div className='flex items-end gap-4'>
                                <CountUp
                                    value={Number(reviewStats.rating)}
                                    decimals={1}
                                    className='font-heading text-[5.5rem] font-semibold leading-[0.8] tracking-[-0.06em]'
                                />

                                <div className='pb-1'>
                                    <Stars />
                                    <p className='mt-2 font-mono text-[12px] text-cream/55'>overall average</p>
                                </div>
                            </div>

                            <ul className='mt-10 space-y-5'>
                                {reviewStats.mentions.map(mention => (
                                    <li key={mention.label}>
                                        <div className='flex justify-between text-[14px]'>
                                            <span>{mention.label}</span>
                                            <span className='font-mono text-cream/55'>{mention.count}</span>
                                        </div>

                                        <div className='mt-2 h-1 overflow-hidden rounded-full bg-cream/10'>
                                            <div className='h-full rounded-full bg-ember' style={{ width: `${(mention.count / topMention) * 100}%` }} />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>

                        <Reveal selector=':scope > *' className='grid gap-4 md:grid-cols-3 lg:col-span-8'>
                            {aboutReviews.map(review => (
                                <ReviewCard key={review.name} review={review} />
                            ))}
                        </Reveal>
                    </div>
                </Container>
            </section>

            <section id='visit' className='py-24 md:py-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:items-start lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            layout='stack'
                            eyebrow="Find us"
                            title="Home base: Foundry Street."
                            titleClassName='max-w-[12ch]'
                            description="All six trucks pull out of the Riverton yard at 7 AM. Our technicians are our own employees, in uniform and in marked trucks. We never use subcontractors."
                        />
                    </div>

                    <Reveal selector=':scope > li' as='ul' className='grid gap-px overflow-hidden rounded-[28px] border border-line bg-line sm:grid-cols-2 lg:col-span-7'>
                        {visitDetails.map((detail, index) => {
                            const content = (
                                <>
                                    <span className='flex size-10 items-center justify-center rounded-full bg-sand text-ink'>
                                        <detail.icon className='size-4' aria-hidden='true' />
                                    </span>

                                    <span className='mt-8 block font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>{detail.label}</span>
                                    <span className='mt-2 block text-[16px] font-medium leading-snug text-ink'>{detail.value}</span>
                                </>
                            )

                            return (
                                <li key={detail.label} className={cn('bg-card', index === visitDetails.length - 1 && 'sm:col-span-2')}>
                                    {detail.href ? (
                                        <a href={detail.href} className='block h-full p-6 transition-colors hover:bg-paper sm:p-7'>
                                            {content}
                                        </a>
                                    ) : (
                                        <div className='h-full p-6 sm:p-7'>{content}</div>
                                    )}
                                </li>
                            )
                        })}
                    </Reveal>
                </Container>
            </section>

            <CtaBand
                title="See the price before we reach for a tool."
                body="Describe the door and we'll text a price range back, usually within 30 minutes."
                primary={{ label: "Request a price", href: QUOTE_PATH }}
            />
        </Layout>
    )
}

export default AboutPage
